import type { ApolloClient } from '@apollo/client';
import type { NetworkError } from '@apollo/client/errors';
import * as Sentry from '@sentry/react';

import type { GraphQLErrorLike } from './apolloErrorHelpers';
import { addAuthBreadcrumb, login, logout, withConcurrentAuthFailureGuard } from './auth';

const GRAPHQL_UNAUTHENTICATED_CODE = 'UNAUTHENTICATED';
const GRAPHQL_ACCOUNT_DELETED_CODE = 'ACCOUNT_DELETED';
const GRAPHQL_USER_NOT_FOUND_CODE = 'USER_NOT_FOUND';

const RECOVERY_ATTEMPTS_STORAGE_KEY = 'staleSessionRecoveryAttemptTimestamps';
const RECOVERY_ATTEMPTS_WINDOW_MS = 60_000;
const MAX_RECOVERY_ATTEMPTS_PER_WINDOW = 2;

type ApolloErrorShapes = {
  graphQLErrors?: ReadonlyArray<GraphQLErrorLike>;
  networkError?: NetworkError;
};

function getErrorCodeFromApolloError(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('extensions' in error)) {
    return undefined;
  }
  const { extensions } = error;
  if (typeof extensions !== 'object' || extensions === null || !('code' in extensions)) {
    return undefined;
  }
  const { code } = extensions;
  return typeof code === 'string' ? code : undefined;
}

// Apollo Client classifies any non-2xx response as a networkError (ServerError)
// with the parsed GraphQL response body on `result`.
function getErrorsFromNetworkErrorBody(networkError: NetworkError | undefined): unknown[] {
  if (!networkError) return [];
  if (!('result' in networkError)) return [];
  const { result } = networkError;
  if (typeof result !== 'object' || result === null) return [];
  if (!('errors' in result) || !Array.isArray(result.errors)) return [];
  return result.errors;
}

function doesApolloErrorContainCode(errorShapes: ApolloErrorShapes, code: string): boolean {
  if (errorShapes.graphQLErrors?.some((error) => getErrorCodeFromApolloError(error) === code)) {
    return true;
  }
  return getErrorsFromNetworkErrorBody(errorShapes.networkError).some(
    (error) => getErrorCodeFromApolloError(error) === code,
  );
}

/**
 * Auth failures arrive two ways, both carrying an extensions code:
 * - viewer resolution fails before resolvers run → non-2xx networkError, errors in body
 * - authorize-guarded field queried as guest → field-level graphQLError
 */
export function isUnauthorizedQueryError(errorShapes: ApolloErrorShapes): boolean {
  return doesApolloErrorContainCode(errorShapes, GRAPHQL_UNAUTHENTICATED_CODE);
}

/**
 * Detects terminal account states — re-authentication cannot recover these
 * because the WorkOS token is valid but the corresponding app user is gone.
 */
export function isTerminalAccountError(errorShapes: ApolloErrorShapes): boolean {
  return (
    doesApolloErrorContainCode(errorShapes, GRAPHQL_ACCOUNT_DELETED_CODE) ||
    doesApolloErrorContainCode(errorShapes, GRAPHQL_USER_NOT_FOUND_CODE)
  );
}

function readRecentRecoveryAttempts(): number[] {
  let parsedRecoveryAttempts: unknown;
  try {
    const rawRecoveryAttempts = window.sessionStorage.getItem(RECOVERY_ATTEMPTS_STORAGE_KEY);
    if (!rawRecoveryAttempts) return [];
    parsedRecoveryAttempts = JSON.parse(rawRecoveryAttempts);
  } catch {
    return [];
  }
  if (!Array.isArray(parsedRecoveryAttempts)) return [];
  const recoveryWindowStart = Date.now() - RECOVERY_ATTEMPTS_WINDOW_MS;
  return parsedRecoveryAttempts.filter(
    (timestamp): timestamp is number =>
      typeof timestamp === 'number' && timestamp > recoveryWindowStart,
  );
}

function recordRecoveryAttempt(recentAttempts: number[]): void {
  try {
    window.sessionStorage.setItem(
      RECOVERY_ATTEMPTS_STORAGE_KEY,
      JSON.stringify([...recentAttempts, Date.now()]),
    );
  } catch {
    // best effort
  }
}

function clearRecoveryAttempts(): void {
  try {
    window.sessionStorage.removeItem(RECOVERY_ATTEMPTS_STORAGE_KEY);
  } catch {
    // best effort
  }
}

// Each recovery redirect is a fresh page load, so only sessionStorage survives to count attempts.
function forceLogoutToBreakRecoveryRedirectLoop(redirect: string, recentAttempts: number[]): void {
  addAuthBreadcrumb('graphql_unauthorized_recovery_loop_detected', {
    redirect,
    recentAttempts,
  });
  Sentry.captureMessage('Stale-session recovery loop detected — forcing logout', 'warning');
  clearRecoveryAttempts();
  void logout();
}

/**
 * In the common case (WorkOS still has the user signed in), the silent re-auth
 * shows a brief flash through /callback. Worst case the user lands at the
 * WorkOS login page — same end-state as a full logout, with better UX in
 * every other case.
 */
export function recoverFromStaleSession(
  client: Pick<ApolloClient<unknown>, 'clearStore'>,
): void {
  const redirect = `${window.location.pathname}${window.location.search}`;

  withConcurrentAuthFailureGuard(() => {
    const recentAttempts = readRecentRecoveryAttempts();

    if (recentAttempts.length >= MAX_RECOVERY_ATTEMPTS_PER_WINDOW) {
      forceLogoutToBreakRecoveryRedirectLoop(redirect, recentAttempts);
      return;
    }

    recordRecoveryAttempt(recentAttempts);
    addAuthBreadcrumb('graphql_unauthorized_stale_session_recovery', { redirect });
    client
      .clearStore()
      .catch((error) => {
        Sentry.captureException(error, {
          tags: { recovery: 'clearStoreFailed' },
          extra: { redirect },
        });
      })
      .finally(() => {
        login({ redirect, reason: 'graphql-unauthorized-stale-session' });
      });
  });
}

export function logoutFromTerminalAccountError(): void {
  withConcurrentAuthFailureGuard(() => {
    void logout();
  });
}
